import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { rollupIdenticalWindows } from '../utils/rollup-windows';
import { uploadJobPDF } from './pdf-storage';

const BROWN     = [139, 69, 19];
const BROWN_L   = [200, 160, 110];
const TEAL      = [0, 105, 92];
const TEAL_BG   = [224, 247, 242];
const TEAL_TEXT = [0, 77, 64];
const WHITE     = [255, 255, 255];
const LIGHT     = [240, 240, 240];
const DARK      = [40, 40, 40];
const LABEL     = [100, 60, 20];

const PAGE_W = 279.4;
const PAGE_H = 215.9;
const MARGIN = 10;
const HDR_H  = 38;
const FTR_H  = 10;

// ─── FOOTER LAYOUT PRESETS ────────────────────────────────────────────────
// After the table ends, we still need to draw: totals (subtotal/tax/grand
// total) AND the bottom info box ("How did you find out about us?" etc.).
// Three presets so we can always fit on the same page when there's any room
// at all, and only start a fresh page in genuinely extreme cases.
//
// totalSpaceNeeded(G, taxEnabled) sums the post-table footprint so we can
// check it against the actual available space below the table.
const NORMAL = {
  postTableGap: 8, subtotalLineH: 6, taxLineH: 6, separatorGap: 4,
  grandTotalBoxH: 10, postGrandTotalGap: 16, bottomBoxH: 16, safetyMargin: 6,
  fontTotalsLabel: 8, fontGrandTotal: 10,
  fontBottomLabel: 6.5, fontBottomValue: 8,
  bottomLabelY: 6, bottomValueY: 12,
};
const TIGHT = {
  postTableGap: 5, subtotalLineH: 5, taxLineH: 5, separatorGap: 3,
  grandTotalBoxH: 9,  postGrandTotalGap: 12, bottomBoxH: 14, safetyMargin: 4,
  fontTotalsLabel: 8, fontGrandTotal: 9.5,
  fontBottomLabel: 6.5, fontBottomValue: 8,
  bottomLabelY: 5.5, bottomValueY: 11,
};
const ULTRA = {
  postTableGap: 3, subtotalLineH: 4, taxLineH: 4, separatorGap: 2,
  grandTotalBoxH: 8,  postGrandTotalGap: 10, bottomBoxH: 12, safetyMargin: 3,
  fontTotalsLabel: 7.5, fontGrandTotal: 9,
  fontBottomLabel: 6, fontBottomValue: 7.5,
  bottomLabelY: 5, bottomValueY: 10,
};

function totalSpaceNeeded(G, taxEnabled) {
  return G.postTableGap
       + G.subtotalLineH
       + (taxEnabled ? G.taxLineH : 0)
       + G.separatorGap
       + G.postGrandTotalGap
       + G.bottomBoxH
       + G.safetyMargin;
}

const LOGO_URL = '/brand/twp-logo.png';
let _logo = null;
async function getLogo() {
  if (_logo) return _logo;
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return new Promise(r => {
      const fr = new FileReader();
      fr.onloadend = () => { _logo = fr.result; r(_logo); };
      fr.onerror = () => r(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

function fmtDate(val) {
  if (!val) return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (val?.toDate) return val.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (typeof val === 'string' && val.includes('-')) {
    const [y, m, d] = val.split('-');
    return new Date(+y, +m - 1, +d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return String(val);
}

function fmt$(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function chk(val) { return val ? 'YES' : ''; }
function val(v)   { return v && String(v).trim() ? String(v).trim() : ''; }

const WINDOW_STYLES = ['double hung','single hung','casement','awning','slider','picture','bay','bow','garden'];
const WINDOW_CODES  = [' dh',' pw',' csmt',' aw',' 2ls',' 3ls',' bsmt',' bm','^dh$','^pw$','^csmt$','^aw$','^2ls$','^3ls$','^bsmt$','^bm$'];
function isWin(w) {
  if (w.lineType === 'twp_window' || w.type === 'twp_window') return true;
  if (w.lineType === 'ordered_window' || w.type === 'ordered_window') return true;
  if (w.isMulledPane || w.isMulledUnit) return true;
  const d = (w.style || w.description || '').toLowerCase().trim();
  if (!d) return false;
  if (WINDOW_STYLES.some(s => d.includes(s))) return true;
  if (WINDOW_CODES.some(c => {
    if (c.startsWith('^')) return new RegExp(c).test(d);
    return d.includes(c) || d.startsWith(c.trim());
  })) return true;
  return false;
}

function drawContinuationHeader(doc, jo, pageNum, totalPages) {
  const BANNER_H = 10;
  doc.setFillColor(...WHITE);
  doc.rect(0, 0, PAGE_W, BANNER_H, 'F');
  doc.setFillColor(...BROWN);
  doc.rect(0, 0, 3, BANNER_H, 'F');
  doc.setDrawColor(...BROWN_L);
  doc.setLineWidth(0.4);
  doc.line(0, BANNER_H, PAGE_W, BANNER_H);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...TEAL);
  doc.text('JOB ORDER', MARGIN + 4, 6.5);
  doc.setTextColor(...BROWN);
  doc.text(`#${jo.contractNumber || ''}`, MARGIN + 24, 6.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...LABEL);
  doc.text('— continued —', MARGIN + 38, 6.5);

  const customerName = (jo.customer && jo.customer.name) || '';
  if (customerName) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...LABEL);
    doc.text(customerName, PAGE_W / 2, 6.5, { align: 'center' });
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...DARK);
  doc.text(`Page ${pageNum} of ${totalPages}`, PAGE_W - MARGIN - 2, 6.5, { align: 'right' });
}

function drawHeader(doc, jo, logo, pageNum, totalPages) {
  doc.setFillColor(...WHITE);
  doc.rect(0, 0, PAGE_W, HDR_H, 'F');
  doc.setFillColor(...BROWN);
  doc.rect(0, 0, 3, HDR_H, 'F');
  doc.setDrawColor(...BROWN_L);
  doc.setLineWidth(0.6);
  doc.line(0, HDR_H, PAGE_W, HDR_H);

  if (logo) { try { doc.addImage(logo, 'PNG', MARGIN + 4, 5, 16, 16); } catch {} }

  doc.setTextColor(...BROWN);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 24, 12);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...LABEL);
  doc.text('1300 E. 4th Ave  •  Milbank, South Dakota 57252', MARGIN + 24, 17);
  doc.text('(605) 432-1834  •  Toll Free (877) 224-0745  •  Fax (866) 432-1718', MARGIN + 24, 21.5);

  doc.setFontSize(17);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEAL);
  doc.text('JOB ORDER', PAGE_W - MARGIN, 13, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BROWN);
  doc.text(`Contract #${jo.contractNumber}`, PAGE_W - MARGIN, 21, { align: 'right' });

  const c = jo.customer || {};
  const fields = [
    { label: 'Name',       value: c.name       || '',                                    width: 38 },
    { label: 'Address',    value: c.address    || '',                                    width: 40 },
    { label: 'City',       value: c.city       || '',                                    width: 22 },
    { label: 'State',      value: c.state      || '',                                    width: 12 },
    { label: 'Zip',        value: c.zip        || '',                                    width: 16 },
    { label: 'Home Ph',    value: c.phone      || '',                                    width: 22 },
    { label: 'Cell Ph',    value: c.cellPhone  || '',                                    width: 22 },
    { label: 'Work Ph',    value: c.workPhone  || '',                                    width: 22 },
    { label: 'Order Date', value: fmtDate(jo.orderDate || jo.createdAt),                 width: 22 },
    { label: 'Page',       value: `${pageNum} of ${totalPages}`,                          width: 14 },
  ];

  function fitText(value, maxWidth) {
    const s = String(value || '');
    if (!s) return '';
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    if (doc.getTextWidth(s) <= maxWidth) return s;
    let trimmed = s;
    while (trimmed.length > 1 && doc.getTextWidth(trimmed + '…') > maxWidth) {
      trimmed = trimmed.slice(0, -1);
    }
    return trimmed + '…';
  }

  let fx = MARGIN + 4;
  fields.forEach(({ label, value, width }) => {
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...LABEL);
    doc.text(label + ':', fx, 28);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...DARK);
    doc.setFontSize(6.5);
    doc.text(fitText(value, width - 2), fx, 33);
    fx += width;
  });
}

function drawFooter(doc, jo, pageNum, totalPages) {
  const y = PAGE_H - FTR_H;
  doc.setFillColor(...WHITE);
  doc.rect(0, y, PAGE_W, FTR_H, 'F');
  doc.setFillColor(...BROWN);
  doc.rect(0, y, 3, FTR_H, 'F');
  doc.setDrawColor(...BROWN_L);
  doc.setLineWidth(0.6);
  doc.line(0, y, PAGE_W, y);
  const now = new Date();
  const printed = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setTextColor(...LABEL);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Contract #${jo.contractNumber}  •  ${jo.customer?.name || ''}  •  Printed: ${printed}  •  Page ${pageNum} of ${totalPages}`,
    PAGE_W / 2, y + 6.5, { align: 'center' }
  );
}

export async function generateJobOrderPDF(jo, returnBlob = false) {
  const logo = await getLogo();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
  const windows = jo.windows || [];
  const adderRows = (jo.adders || []).map(a => ({
    lineType: 'adder',
    type: 'adder',
    qty: a.qty || 1,
    style: a.name || a.description || '',
    description: a.name || a.description || '',
    unitPrice: a.price || 0,
    price: a.price || 0,
    extrasNotes: a.notes || '',
  }));
  const allRows = rollupIdenticalWindows([...windows, ...adderRows]);

  drawHeader(doc, jo, logo, 1, 1);
  drawFooter(doc, jo, 1, 1);

  const head = [[
    'Qty',
    'Width × Height',
    'UI',
    'Outside\nColor',
    'Inside\nColor',
    'Style',
    'Glass',
    '',
    '',
    '',
    '',
    '',
    '',
    'Room',
    'Extras / Notes',
    'Unit Price',
    'Total Price'
  ]];

  const ROTATED_HEADERS = {
    7:  'Flanges',
    8:  'Colonial',
    9:  'Diamond',
    10: 'Prairie',
    11: '1/2',
    12: 'Full',
  };

  const body = allRows.map(w => {
    const ww    = parseFloat(w.width)  || 0;
    const hh    = parseFloat(w.height) || 0;
    const sizeS = ww && hh ? `${w.width}" × ${w.height}"` : (w.width ? `${w.width}"` : w.height ? `${w.height}"` : '');
    const uiV   = ww && hh ? String(ww + hh) : '';
    const unit  = parseFloat(w.unitPrice) || parseFloat(w.price) || 0;
    const total = unit * (parseFloat(w.qty) || 1);
    const win   = isWin(w);
    const isAdder = w.lineType === 'adder' || w.type === 'adder';

    if (isAdder) {
      return [
        { content: w.qty || 1, styles: { halign: 'center' } },
        { content: val(w.style || w.description || w.extrasNotes), colSpan: 14,
          styles: { halign: 'left', fontStyle: 'italic', textColor: [0, 100, 50], fillColor: [240, 252, 244] } },
        { content: unit > 0 ? fmt$(unit) : '', styles: { halign: 'right', textColor: LABEL } },
        { content: total > 0 ? fmt$(total) : '', styles: { halign: 'right', fontStyle: 'bold', textColor: BROWN } },
      ];
    }

    const isNote = w.lineType === 'note' || w.type === 'note';
    if (isNote) {
      return [
        { content: `Note: ${val(w.description || w.style || '')}`, colSpan: 17,
          styles: { halign: 'left', fontStyle: 'italic', textColor: [71, 85, 105], fillColor: [248, 250, 252] } },
      ];
    }

    return [
      w.qty || 1,
      sizeS,
      uiV,
      val(w.colorOutside),
      val(w.colorInside),
      val(w.style || w.description),
      (w.lineType === 'twp_window' || (!w.lineType && win)) ? val(w.glassPkg || w.glass || 'DLA') : '',
      win ? chk(w.flanges)      : '',
      win ? chk(w.gridColonial) : '',
      win ? chk(w.gridDiamond)  : '',
      win ? chk(w.gridPrairie)  : '',
      win ? chk(w.screenHalf)   : '',
      win ? chk(w.screenFull)   : '',
      val(w.room),
      val(w.extrasNotes),
      unit  > 0 ? fmt$(unit)  : '',
      total > 0 ? fmt$(total) : '',
    ];
  });

  let currentPage = 1;

  autoTable(doc, {
    startY: HDR_H + 4,
    head,
    body,
    styles: {
      fontSize: 7,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      textColor: DARK,
      lineColor: LIGHT,
      lineWidth: 0.15,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: TEAL_BG,
      textColor: TEAL_TEXT,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'center',
      valign: 'middle',
      minCellHeight: 12,
    },
    alternateRowStyles: { fillColor: [245, 251, 249] },
    columnStyles: {
      0:  { cellWidth: 8,    halign: 'center' },
      1:  { cellWidth: 24,   halign: 'center' },
      2:  { cellWidth: 10,   halign: 'center' },
      3:  { cellWidth: 17,   halign: 'center' },
      4:  { cellWidth: 17,   halign: 'center' },
      5:  { cellWidth: 30,   halign: 'left'   },
      6:  { cellWidth: 18,   halign: 'center' },
      7:  { cellWidth: 6,    halign: 'center' },
      8:  { cellWidth: 6,    halign: 'center' },
      9:  { cellWidth: 6,    halign: 'center' },
      10: { cellWidth: 6,    halign: 'center' },
      11: { cellWidth: 6,    halign: 'center' },
      12: { cellWidth: 6,    halign: 'center' },
      13: { cellWidth: 18,   halign: 'center' },
      14: { cellWidth: 'auto', halign: 'left', overflow: 'linebreak',
            cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 } },
      15: { cellWidth: 18,   halign: 'right', textColor: LABEL },
      16: { cellWidth: 18,   halign: 'right', fontStyle: 'bold', textColor: BROWN },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.cell.raw === 'YES') {
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fillColor = [255, 255, 255];
      }
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.cell.raw === 'YES') {
        const x = data.cell.x + data.cell.width / 2;
        const y = data.cell.y + data.cell.height / 2 + 1.5;
        doc.setFont('ZapfDingbats');
        doc.setFontSize(10);
        doc.setTextColor(0, 140, 0);
        doc.text('4', x, y, { align: 'center' });
        doc.setFont('helvetica');
        doc.setTextColor(0, 0, 0);
      }
      if (data.section === 'head' && ROTATED_HEADERS[data.column.index]) {
        const label = ROTATED_HEADERS[data.column.index];
        const x = data.cell.x + data.cell.width / 2 + 1.2;
        const y = data.cell.y + data.cell.height - 1.5;
        doc.saveGraphicsState();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...TEAL_TEXT);
        doc.text(label, x, y, { align: 'left', angle: 90 });
        doc.restoreGraphicsState();
      }
    },
    margin: { left: MARGIN, right: MARGIN, top: 14, bottom: FTR_H + 8 },
    didDrawPage: (data) => {
      currentPage = data.pageNumber;
      if (data.pageNumber > 1) {
        drawContinuationHeader(doc, jo, data.pageNumber, 1);
        drawFooter(doc, jo, data.pageNumber, 1);
      }
    },
  });

  // ─── Choose footer layout preset based on actual remaining space ─────────
  // Pick the loosest preset that fits below the table on the current page.
  // If even the tightest preset doesn't fit, start a fresh page (using
  // NORMAL since there's plenty of room up there).
  const finalY = doc.lastAutoTable.finalY;
  const availableBelowTable = (PAGE_H - FTR_H) - finalY;

  let G;
  let y;
  if (totalSpaceNeeded(NORMAL, jo.taxEnabled) <= availableBelowTable) {
    G = NORMAL;
    y = finalY + G.postTableGap;
  } else if (totalSpaceNeeded(TIGHT, jo.taxEnabled) <= availableBelowTable) {
    G = TIGHT;
    y = finalY + G.postTableGap;
  } else if (totalSpaceNeeded(ULTRA, jo.taxEnabled) <= availableBelowTable) {
    G = ULTRA;
    y = finalY + G.postTableGap;
  } else {
    // Genuinely no room — start a fresh page with the spacious NORMAL layout
    doc.addPage(); currentPage++;
    if (currentPage > 1) {
      drawContinuationHeader(doc, jo, currentPage, 1);
    } else {
      drawHeader(doc, jo, logo, currentPage, 1);
    }
    drawFooter(doc, jo, currentPage, 1);
    G = NORMAL;
    y = HDR_H + 8;
  }

  // ─── Totals block ────────────────────────────────────────────────────────
  const totX = PAGE_W - MARGIN - 72;

  doc.setFontSize(G.fontTotalsLabel);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...LABEL);
  doc.text('Subtotal', totX, y);
  doc.setTextColor(...DARK);
  doc.text(fmt$(jo.subtotal), PAGE_W - MARGIN, y, { align: 'right' });
  y += G.subtotalLineH;

  if (jo.taxEnabled) {
    doc.setTextColor(...LABEL);
    const taxLabel = jo.taxRate === '6.2' ? `Sales Tax (6.2%)` : `SD Excise Tax (2.041%)`;
    doc.text(taxLabel, totX, y);
    doc.setTextColor(...DARK);
    doc.text(fmt$(jo.taxAmount), PAGE_W - MARGIN, y, { align: 'right' });
    y += G.taxLineH;
  }

  doc.setDrawColor(...BROWN_L);
  doc.setLineWidth(0.4);
  doc.line(totX, y, PAGE_W - MARGIN, y);
  y += G.separatorGap;

  doc.setFillColor(...TEAL_BG);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.roundedRect(totX - 2, y - 1, 76, G.grandTotalBoxH, 2, 2, 'FD');
  doc.setFontSize(G.fontGrandTotal);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEAL_TEXT);
  const grandTextY = y + G.grandTotalBoxH - 3;  // baseline near bottom of box
  doc.text('GRAND TOTAL', totX + 2, grandTextY);
  doc.setTextColor(...BROWN);
  doc.text(fmt$(jo.grandTotal), PAGE_W - MARGIN - 1, grandTextY, { align: 'right' });
  y += G.postGrandTotalGap;

  // ─── Bottom info box — always drawn ──────────────────────────────────────
  doc.setFillColor(248, 248, 248);
  doc.setDrawColor(...BROWN_L);
  doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, y, PAGE_W - MARGIN * 2, G.bottomBoxH, 2, 2, 'FD');

  doc.setFontSize(G.fontBottomLabel);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...LABEL);
  doc.text('How did you find out about us?', MARGIN + 4, y + G.bottomLabelY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...DARK);
  doc.setFontSize(G.fontBottomValue);
  doc.text(jo.howDidYouHear || '', MARGIN + 4, y + G.bottomValueY);

  const cwX = PAGE_W / 2 - 20;
  doc.setFontSize(G.fontBottomLabel);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...LABEL);
  doc.text('Casing Wraps:', cwX, y + G.bottomLabelY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...DARK);
  doc.setFontSize(G.fontBottomValue);
  doc.text(
    jo.casingWraps
      ? (jo.casingWrapsColor ? 'Yes — ' + jo.casingWrapsColor : 'Yes')
      : 'No',
    cwX, y + G.bottomValueY
  );

  const odX = PAGE_W - MARGIN - 50;
  doc.setFontSize(G.fontBottomLabel);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...LABEL);
  doc.text('Order Date:', odX, y + G.bottomLabelY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...DARK);
  doc.setFontSize(G.fontBottomValue);
  doc.text(fmtDate(jo.orderDate || jo.createdAt), odX, y + G.bottomValueY);

  // ─── Finalize page numbers ───────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    if (p === 1) {
      drawHeader(doc, jo, logo, p, totalPages);
    } else {
      drawContinuationHeader(doc, jo, p, totalPages);
    }
    drawFooter(doc, jo, p, totalPages);
  }

  const blob = doc.output('blob');

  if (jo.contractNumber) {
    uploadJobPDF(jo.contractNumber, 'joborder', blob).catch(err =>
      console.warn('Job order PDF storage save failed (non-blocking):', err)
    );
  }

  const safeName = (jo.customer?.name || 'Customer').replace(/[^a-zA-Z0-9 ]/g, '').trim();
  if (returnBlob) return blob;
  doc.save(`JobOrder_${jo.contractNumber}_${safeName}.pdf`);
}
